import { inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { IData, IDataPagination } from 'app/core/models/api/data';
import { environment } from '../../../../environments/environment';
import { catchError } from 'rxjs/operators';
import { IError } from 'app/core/models/error/error';
import { ICreatePatient, IGetAllClinicPatient, IGetPatient, IPatient, IUpdatePatient } from 'app/core/models/patient/patient';

const urlGetPatients = () => ['clinic', 'admin', 'patient'];
const urlUpdatePatients = (patientId: string) => ['clinic', 'admin', 'patient', patientId];
export const urlGetAllClinicPatient = () => ['clinic', 'admin', 'patient'];
export const urlGetAllClinicPatientSelect = () => ['clinic', 'admin', 'patient', 'select'];

@Service()
export class PatientService {
    private readonly router = inject(Router);
    private readonly httpClient = inject(HttpClient);

    public getAllClinicPatientSelect(data: IGetAllClinicPatient): Observable<IDataPagination<IPatient[]>> {
        const url = this.router.createUrlTree(urlGetAllClinicPatient(), {
            queryParams: data,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IDataPagination<IPatient[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    getPatients(parameters: IGetPatient): Observable<IDataPagination<IPatient[]>> {
        const url = this.router.createUrlTree(urlGetPatients(), {
            queryParams: parameters,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IDataPagination<IPatient[]>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    createPatient(parameters: ICreatePatient): Observable<IData<IPatient>> {
        const url = this.router.createUrlTree(urlGetPatients());

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<IData<IPatient>>(parsedUrl, parameters, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    updatePatient(parameters: IUpdatePatient): Observable<IData<IPatient>> {
        const { id, ...data } = parameters;

        const url = this.router.createUrlTree(urlUpdatePatients(id));

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.put<IData<IPatient>>(parsedUrl, data, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    deletePatient(id: string): Observable<IData<IPatient>> {
        const url = this.router.createUrlTree(urlUpdatePatients(id));
        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.delete<null>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }
}
